import { members as membersApi } from '../../api/endpoints';
import { isVoided } from '../../utils/records';
import type { Family, Member } from '../../types';

// Keeps the family register and the member register agreed on who the head is
// when a family is EDITED.
//
// The app holds one invariant: a family's `headOfFamily` name equals the `name`
// of the single member in that family whose `relationToHead === 'Head'`.
// Creation maintains it directly — the head is created as a full member from the
// Members Add (Head mode). This keeps it true when the head's details are later
// changed from the Families edit form: the existing head member is renamed to
// match. A head is never resurrected or invented here — swapping the head to a
// different person is a deliberate act done from the Members tab, not a side
// effect of retyping the family's head field.

/** The family fields a head member mirrors. */
export interface HeadForm {
  headOfFamily?: string;
  headGender?: string;
  contactNumber?: string;
  contact?: string;
}

const familyKeyOf = (family: Family): string =>
  family.familyId || `FAM-${family.id}`;

const isHead = (relation?: string): boolean =>
  String(relation || '').toLowerCase() === 'head';

/**
 * On a family edit, mirrors the head's name / gender / mobile onto the family's
 * existing live Head member. Returns the updated member, or null when there is
 * no head member or nothing changed. Rejects if the write fails — the caller
 * decides how to surface that without unwinding the family save.
 */
export const syncHeadMemberOnEdit = async (
  savedFamily: Family,
  form: HeadForm,
  allMembersRaw: Member[]
): Promise<Member | null> => {
  const name = String(form.headOfFamily || '').trim();
  if (!name) return null;

  const familyKey = familyKeyOf(savedFamily);
  const headFields: Partial<Member> = {
    name,
    gender: (form.headGender || undefined) as Member['gender'],
    mobileNumber: form.contactNumber || form.contact || ''
  };

  const existingHead = allMembersRaw.find(
    (m) => !isVoided(m) && m.familyId === familyKey && isHead(m.relationToHead)
  );
  if (!existingHead) return null;

  const unchanged =
    existingHead.name === headFields.name
    && (existingHead.gender || undefined) === headFields.gender
    && (existingHead.mobileNumber || '') === headFields.mobileNumber;
  if (unchanged) return null;

  return membersApi.update(existingHead.id, { ...existingHead, ...headFields });
};

export default syncHeadMemberOnEdit;
